import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleNotch, X, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { fetchMyMatters, startMyThread } from '@/lib/solicitorPortalApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

/**
 * Start a conversation with chambers.
 *
 * The solicitor chooses a MATTER, never a person. Who receives it is decided on the server
 * from that matter's own team, which is what keeps this screen from becoming a directory of
 * Jurivoxa users — a "new message" dialog is the obvious place for that to leak.
 */
export function StartConversationModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (threadId: string) => void;
}) {
  const qc = useQueryClient();
  const [matterId, setMatterId] = useState('');
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');

  const mattersQ = useQuery({
    queryKey: ['portal', 'matters'],
    queryFn: () => fetchMyMatters(),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () => startMyThread({ matterId, subject: subject.trim(), text: text.trim() }),
    onSuccess: (thread) => {
      toast.success('Message sent to chambers');
      // Must match the key MessagesPage uses, or the new conversation does not appear
      // until the solicitor reloads - which reads as "my message vanished".
      qc.invalidateQueries({ queryKey: ['portal', 'threads'] });
      reset();
      onCreated?.(thread.id);
      onClose();
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  function reset() {
    setMatterId('');
    setSubject('');
    setText('');
  }

  if (!open) return null;

  const matters = mattersQ.data ?? [];
  const canSubmit = Boolean(matterId) && subject.trim().length > 0 && text.trim().length > 0 && !create.isPending;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (canSubmit) create.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <Card className="my-auto flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-4">
          <div>
            <div className="text-sm font-semibold tracking-tight">Message chambers</div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Your message goes to the counsel and clerk working on the matter you choose.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { reset(); onClose(); }}
            aria-label="Close"
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="contents">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
            <div>
              <label htmlFor="conv-matter" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Matter
              </label>
              {mattersQ.isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CircleNotch size={14} className="animate-spin" />
                  Loading your matters…
                </div>
              ) : matters.length === 0 ? (
                <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3">
                  <WarningCircle size={16} className="mt-0.5 shrink-0 text-amber-500" weight="fill" />
                  <p className="text-xs text-muted-foreground">
                    You have no matters with this chambers yet, so there is nothing to discuss here.
                    Contact chambers directly to instruct them.
                  </p>
                </div>
              ) : (
                <select
                  id="conv-matter"
                  required
                  value={matterId}
                  onChange={(e) => setMatterId(e.target.value)}
                  className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100 dark:focus:ring-brand-900/40"
                >
                  <option value="">Select a matter…</option>
                  {matters.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.caseNumber} — {m.caseName}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label htmlFor="conv-subject" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Subject
              </label>
              <Input
                id="conv-subject"
                required
                maxLength={200}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Trial bundle deadline"
              />
            </div>

            <div>
              <label htmlFor="conv-text" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Message
              </label>
              <textarea
                id="conv-text"
                required
                rows={6}
                maxLength={4000}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Write your message to chambers…"
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100 dark:focus:ring-brand-900/40"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">{text.length}/4000</p>
            </div>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-5 py-3">
            <Button type="button" variant="outline" size="sm" onClick={() => { reset(); onClose(); }} disabled={create.isPending}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={!canSubmit}>
              {create.isPending ? (
                <>
                  <CircleNotch size={13} className="mr-1.5 animate-spin" />
                  Sending…
                </>
              ) : (
                'Send message'
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
